import React, { memo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Input, Menu, Skeleton, Spin } from 'antd';
import { InfiniteScroll } from '@components/InfiniteScroll/InfiniteScroll';
import ApprovalEmptyStateWrapper from '@components/Approval/ApprovalEmptyStateWrapper/ApprovalEmptyStateWrapper';
import PageSortPanel from '@components/PageSortPanel';
import LottieComponent from '@components/Lottie';
import {
  ApprovalWorkflowControllerListApprovalWorkflowsParams,
  ApprovalWorkflowItemDto
} from '@api/Api';
import { ReactComponent as SearchSvg } from '@assets/icons/search.svg';
import StatusesFilter from '@components/Approval/StatusesFilter/StatusesFilter';
import DatesFilter from '@components/Approval/DatesFilter/DatesFilter';
import ApprovalsTable from '@components/Approval/ApprovalsTable/ApprovalsTable';
import CampaignsFilter from '@components/Approval/CampaignsFilter/CampaignsFilter';
import PotentialsManagersFilter from '@components/Approval/PotentialsManagersFilter/PotentialsManagersFilter';

import './ApprovalsManage.less';

type ApprovalsManageProps = {
  approvalsList: ApprovalWorkflowItemDto[];
  approvalWorkflowsParams: Omit<
    ApprovalWorkflowControllerListApprovalWorkflowsParams,
    'workspaceId'
  >;
  isLoading: boolean;
  isFetchingNextPage: boolean;
  fetchNextPageApprovals: () => void;
  refetchApprovals: () => void;
  hasNextPageApprovals: boolean;
};

export default memo(function ApprovalsManage({
  approvalsList,
  approvalWorkflowsParams,
  isLoading,
  isFetchingNextPage,
  fetchNextPageApprovals,
  refetchApprovals,
  hasNextPageApprovals
}: ApprovalsManageProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const timeoutId = useRef<ReturnType<typeof setTimeout>>();
  const sortByParam = (searchParams.get('sortBy') ||
    'default') as ApprovalWorkflowControllerListApprovalWorkflowsParams['orderBy'];

  const hasActiveFilter = Object.values(approvalWorkflowsParams || {}).some(
    (i) => !!i
  );

  return (
    <div className="approvals-manage">
      <div className="approvals-manage_head">
        <div className="approvals-manage_head-filter">
          <CampaignsFilter
            onChange={(statuses) => {
              searchParams.delete('campaignIds');
              if (statuses.length) {
                statuses.forEach((status) =>
                  searchParams.append('campaignIds', status)
                );
              }
              setSearchParams(searchParams);
            }}
            selectedCampaignIds={approvalWorkflowsParams.campaignId || []}
          />
          <DatesFilter
            onChange={(dates) => {
              searchParams.delete('dueDate');
              if (dates?.length) {
                dates.forEach((date) => searchParams.append('dueDate', date));
              }
              setSearchParams(searchParams);
            }}
            date={
              approvalWorkflowsParams.dueDateFrom &&
              approvalWorkflowsParams.dueDateTo
                ? [
                    approvalWorkflowsParams.dueDateFrom,
                    approvalWorkflowsParams.dueDateTo
                  ]
                : []
            }
            placeholder="Deadline"
            timeOptions={['today', 'tomorrow', 'nextWeek', 'nextMonth']}
          />
          <StatusesFilter
            onChange={(statuses) => {
              searchParams.delete('statuses');
              if (statuses.length) {
                statuses.forEach((status) =>
                  searchParams.append('statuses', status)
                );
              }
              setSearchParams(searchParams);
            }}
            selectedStatuses={approvalWorkflowsParams.statuses || []}
          />
          <PotentialsManagersFilter
            onChange={(statuses) => {
              searchParams.delete('managers');
              if (statuses.length) {
                statuses.forEach((status) =>
                  searchParams.append('managers', status)
                );
              }
              setSearchParams(searchParams);
            }}
            selectedManagerEmails={approvalWorkflowsParams.managers || []}
          />
          <DatesFilter
            onChange={(dates) => {
              searchParams.delete('createdDate');
              if (dates?.length) {
                dates.forEach((date) =>
                  searchParams.append('createdDate', date)
                );
              }
              setSearchParams(searchParams);
            }}
            date={
              approvalWorkflowsParams.createdAtFrom &&
              approvalWorkflowsParams.createdAtTo
                ? [
                    approvalWorkflowsParams.createdAtFrom,
                    approvalWorkflowsParams.createdAtTo
                  ]
                : []
            }
            placeholder="Created date"
            timeOptions={['today', 'tomorrow', 'prevWeek', 'prevMonth']}
          />
        </div>
        <div className="approvals-manage_head-sort">
          <PageSortPanel
            isVisibleSortStyle={false}
            entity="approvals"
            sortMenuModal={
              <Menu
                selectedKeys={sortByParam ? [sortByParam] : []}
                style={{
                  minWidth: 260
                }}
                onClick={({ key }) => {
                  searchParams.set('sortBy', key);
                  setSearchParams(searchParams);
                }}
                items={[
                  { label: 'Default', key: 'default' },
                  { label: 'Need to review', key: 'inProgress' },
                  { label: 'Already completed review', key: 'notInProgress' }
                ]}
              />
            }
            searchInputComponent={
              <Input
                style={{ marginRight: 16, padding: '8px 12px' }}
                placeholder="Search"
                prefix={<SearchSvg />}
                size="large"
                defaultValue={approvalWorkflowsParams.searchQuery || ''}
                onChange={(e) => {
                  const _searchText = e.target.value.trim();
                  if (timeoutId.current) clearTimeout(timeoutId.current);
                  timeoutId.current = setTimeout(() => {
                    if (_searchText) searchParams.set('search', _searchText);
                    if (!_searchText) searchParams.delete('search');
                    setSearchParams(searchParams);
                  }, 400);
                }}
              />
            }
          />
        </div>
      </div>
      <div className="approvals-manage_infinite-scroll">
        <InfiniteScroll
          loading={isFetchingNextPage ? 'more' : false}
          next={fetchNextPageApprovals}
          hasMore={hasNextPageApprovals ?? false}
          loader={
            <>
              <Skeleton.Input />
              <Skeleton.Input />
            </>
          }
        >
          {isLoading && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                height: '100%'
              }}
            >
              <Spin size="large" />
            </div>
          )}
          {!approvalsList.length && !!hasActiveFilter && !isLoading && (
            <div className="empty_state empty_state--search">
              <LottieComponent className="icon" view="noSearchingResult" />
              <strong
                className="main-body-text main-body-text--bold"
                style={{ marginBottom: 8 }}
              >
                No results found
              </strong>
              <p className="copy main-body-text main-body-text--dark-gray">
                It seems we can’t find any results based on your search
              </p>
            </div>
          )}
          {!approvalsList.length && !hasActiveFilter && !isLoading && (
            <ApprovalEmptyStateWrapper
              listLength={approvalsList.length}
              refetchApprovalsData={refetchApprovals}
              wholePage={true}
            />
          )}
          {!!approvalsList.length && !isLoading && (
            <ApprovalsTable
              refetchApprovalsData={refetchApprovals}
              approvals={approvalsList}
              isShowCompanyName={sortByParam === 'default'}
            />
          )}
        </InfiniteScroll>
      </div>
    </div>
  );
});
